import { customElement, FASTElement, observable } from '@microsoft/fast-element';
import {
  AGGREGATION_TYPE,
  GRAPH_PERIODICITY,
  GRAPH_SCENARIO,
} from '../../../../../utils/enums/generic.enum';
import { ImpliedCarbonPriceStyles as styles } from './implied-carbon-price.styles';
import {
  OPTIONS_ENUM,
  ImpliedCarbonPriceTemplate as template,
} from './implied-carbon-price.template';
import Highcharts, { Chart } from 'highcharts';
import { IProjectForecastTs } from '../../../../../types';
import {
  getDatesFromData,
  getFormattedNumber,
  getValuesFromData,
} from '../../../../../utils/chart-data-utils';

const name = 'implied-carbon-price';

@customElement({
  name,
  template,
  styles,
})
export class ImpliedCarbonPrice extends FASTElement {
  @observable forecastData: Array<IProjectForecastTs>;

  @observable impliedCarbonPriceChart!: HTMLDivElement;
  @observable periodicity: GRAPH_PERIODICITY = GRAPH_PERIODICITY.QUATERLY;
  @observable selectedScenarios: string[] = [GRAPH_SCENARIO.BASELINE];
  @observable isVisible: boolean = false;
  @observable selectedImpliedCarbonPrice: string[] = [
    OPTIONS_ENUM.MEAN,
    OPTIONS_ENUM.P05,
    OPTIONS_ENUM.P95,
  ];

  private chart: Chart;
  private resizeObserver: ResizeObserver;

  connectedCallback() {
    this.renderImpliedCarbonPrice();
    super.connectedCallback();

    this.resizeObserver = new ResizeObserver((entry) => {
      if (entry[0]?.contentRect && this.chart) {
        this.chart.reflow();
      }
    });

    this.resizeObserver.observe(this.parentElement);
  }

  public updateScenariosOptions(event: CustomEvent) {
    this.selectedScenarios = event.detail;
    this.renderImpliedCarbonPrice();
  }

  public updateImpliedCarbonPricesOptions(event: CustomEvent) {
    this.selectedImpliedCarbonPrice = event.detail;
    this.renderImpliedCarbonPrice();
  }

  public renderGraph(
    forecastData: Array<IProjectForecastTs>,
    startTimestamp: number,
    endTimestamp: number
  ) {
    this.forecastData = forecastData
      ? forecastData.filter(
          (element) => element.METRICS_DATETIME >= startTimestamp && element.METRICS_DATETIME < endTimestamp
        )
      : [];
    this.renderImpliedCarbonPrice();
  }

  public periodicityChanged(oldValue, newValue) {
    this.renderImpliedCarbonPrice();
  }

  public reflow() {
    this.isVisible = false;
    setTimeout(() => {
      this.renderImpliedCarbonPrice();
    }, 1);
  }

  private getDataArrays(): Array<any> {
    const dataArrays: any[] = [];

    this.selectedScenarios.forEach((scenario) => {
      this.selectedImpliedCarbonPrice.forEach((impliedCarbonPrice) => {
        switch (impliedCarbonPrice) {
          case OPTIONS_ENUM.P95:
            dataArrays.push({
              type: 'line',
              zIndex: 2,
              color: Highcharts.getOptions().colors[this.getIndexColor(scenario, OPTIONS_ENUM.P95)],
              yAxis: 0,
              name: `${scenario} P95 (Implied Carbon Price) ($/MT CO₂)`,
              data: getValuesFromData(
                this.forecastData,
                this.getP95Value(scenario),
                this.periodicity,
                this.periodicity !== GRAPH_PERIODICITY.MONTHLY ? AGGREGATION_TYPE.AVG : null
              ),
            });
            break;
          case OPTIONS_ENUM.MEAN:
            dataArrays.push({
              type: 'line',
              zIndex: 2,
              color:
                Highcharts.getOptions().colors[this.getIndexColor(scenario, OPTIONS_ENUM.MEAN)],
              yAxis: 0,
              name: `${scenario} Mean (Implied Carbon Price) ($/MT CO₂)`,
              data: getValuesFromData(
                this.forecastData,
                this.getMeanValue(scenario),
                this.periodicity,
                this.periodicity !== GRAPH_PERIODICITY.MONTHLY ? AGGREGATION_TYPE.AVG : null
              ),
            });
            break;
          case OPTIONS_ENUM.P05:
            dataArrays.push({
              type: 'line',
              zIndex: 2,
              color: Highcharts.getOptions().colors[this.getIndexColor(scenario, OPTIONS_ENUM.P05)],
              yAxis: 0,
              name: `${scenario} P05 (Implied Carbon Price) ($/MT CO₂)`,
              data: getValuesFromData(
                this.forecastData,
                this.getP05Value(scenario),
                this.periodicity,
                this.periodicity !== GRAPH_PERIODICITY.MONTHLY ? AGGREGATION_TYPE.AVG : null
              ),
            });
            break;
          default:
            break;
        }
      });
    });

    this.selectedScenarios.forEach((scenario) => {
      dataArrays.push({
        type: 'area',
        color: Highcharts.getOptions().colors[this.getIndexColor(scenario, OPTIONS_ENUM.MEAN)],
        yAxis: 1,
        zIndex: 1,
        name: `${scenario} Mean ($/MWh)`,
        data: getValuesFromData(
          this.forecastData,
          this.getMeanAreaValue(scenario),
          this.periodicity,
          this.periodicity !== GRAPH_PERIODICITY.MONTHLY ? AGGREGATION_TYPE.AVG : null
        ),
      });
    });

    return dataArrays;
  }

  private renderImpliedCarbonPrice() {
    if (
      !this.forecastData ||
      this.forecastData.length === 0 ||
      this.selectedScenarios.length === 0
    ) {
      this.isVisible = false;
      return;
    }

    this.isVisible = true;

    const dates = getDatesFromData(this.forecastData, this.periodicity);

    const dataArrays = this.getDataArrays();

    this.chart = Highcharts.chart({
      chart: {
        renderTo: this.impliedCarbonPriceChart,
      },
      title: {
        text: '',
      },
      xAxis: {
        categories: dates,
      },
      yAxis: [
        {
          // Primary yAxis
          title: {
            text: '$/MT CO₂',
          },
          labels: {
            formatter: function () {
              return getFormattedNumber(this.value);
            },
          },
        },
        {
          // Secondary yAxis
          title: {
            text: '$/MWh',
            rotation: -90,
          },
          labels: {
            formatter: function () {
              return getFormattedNumber(this.value);
            },
          },
          opposite: true,
        },
      ],
      tooltip: {
        formatter: function () {
          return this.points.reduce(function (s, point) {
            return s + '<br/>' + point.series.name + ': ' + getFormattedNumber(point.y);
          }, '<b>' + this.x + '</b>');
        },
        valueDecimals: 2,
      },
      series: dataArrays,
    });
  }

  private getP05Value(scenario): string {
    switch (scenario) {
      case GRAPH_SCENARIO.BASELINE:
        return 'SETTLEMENT_ICP_BASELINE_P05_USD_PER_MT_CO2E';
      case GRAPH_SCENARIO.HIGH_GAS:
        return 'SETTLEMENT_ICP_HIGH_GAS_P05_USD_PER_MT_CO2E';
      case GRAPH_SCENARIO.LOW_GAS:
        return 'SETTLEMENT_ICP_LOW_GAS_P05_USD_PER_MT_CO2E';
      default:
        return null;
    }
  }

  private getMeanValue(scenario): string {
    switch (scenario) {
      case GRAPH_SCENARIO.BASELINE:
        return 'SETTLEMENT_ICP_BASELINE_MEAN_USD_PER_MT_CO2E';
      case GRAPH_SCENARIO.HIGH_GAS:
        return 'SETTLEMENT_ICP_HIGH_GAS_MEAN_USD_PER_MT_CO2E';
      case GRAPH_SCENARIO.LOW_GAS:
        return 'SETTLEMENT_ICP_LOW_GAS_MEAN_USD_PER_MT_CO2E';
      default:
        return null;
    }
  }

  private getP95Value(scenario): string {
    switch (scenario) {
      case GRAPH_SCENARIO.BASELINE:
        return 'SETTLEMENT_ICP_BASELINE_P95_USD_PER_MT_CO2E';
      case GRAPH_SCENARIO.HIGH_GAS:
        return 'SETTLEMENT_ICP_HIGH_GAS_P95_USD_PER_MT_CO2E';
      case GRAPH_SCENARIO.LOW_GAS:
        return 'SETTLEMENT_ICP_LOW_GAS_P95_USD_PER_MT_CO2E';
      default:
        return null;
    }
  }

  private getMeanAreaValue(scenario): string {
    switch (scenario) {
      case GRAPH_SCENARIO.BASELINE:
        return 'SETTLEMENT_BASELINE_MEAN_USD_PER_MWH';
      case GRAPH_SCENARIO.HIGH_GAS:
        return 'SETTLEMENT_HIGH_GAS_MEAN_USD_PER_MWH';
      case GRAPH_SCENARIO.LOW_GAS:
        return 'SETTLEMENT_LOW_GAS_MEAN_USD_PER_MWH';
      default:
        return null;
    }
  }

  private getIndexColor(scenario: string, option: OPTIONS_ENUM): number {
    let index: number;
    switch (scenario) {
      case GRAPH_SCENARIO.BASELINE:
        index = 0;
        break;
      case GRAPH_SCENARIO.HIGH_GAS:
        index = 3;
        break;
      case GRAPH_SCENARIO.LOW_GAS:
        index = 6;
        break;
      default:
        index = 0;
        break;
    }
    switch (option) {
      case OPTIONS_ENUM.MEAN:
        index = index + 0;
        break;
      case OPTIONS_ENUM.P05:
        index = index + 1;
        break;
      case OPTIONS_ENUM.P95:
        index = index + 2;
        break;

      default:
        index = index + 0;
        break;
    }
    return index;
  }
}
